/**
 * Generic storage utilities for working with JSON data
 */

import { getFromLocalStorage, setToLocalStorage, removeFromLocalStorage } from './localStorage'
import { getFromSessionStorage, setToSessionStorage, removeFromSessionStorage } from './sessionStorage'

/**
 * Storage type enum
 */
export enum StorageType {
  LOCAL = 'local',
  SESSION = 'session',
}

/**
 * Safely stores a JSON object in the specified storage
 * @param key - The storage key
 * @param value - The object to store
 * @param storageType - The type of storage to use
 */
export const setJsonToStorage = <T>(key: string, value: T, storageType: StorageType = StorageType.SESSION): void => {
  try {
    const jsonString = JSON.stringify(value)
    if (storageType === StorageType.LOCAL) {
      setToLocalStorage(key, jsonString)
    } else {
      setToSessionStorage(key, jsonString)
    }
  } catch {
    // Silently fail if JSON serialization fails
  }
}

/**
 * Safely retrieves and parses a JSON object from the specified storage
 * @param key - The storage key
 * @param storageType - The type of storage to use
 * @returns The parsed object or null if not available
 */
export const getJsonFromStorage = <T>(key: string, storageType: StorageType = StorageType.SESSION): T | null => {
  try {
    const jsonString = storageType === StorageType.LOCAL ? getFromLocalStorage(key) : getFromSessionStorage(key)

    if (!jsonString) return null

    return JSON.parse(jsonString) as T
  } catch {
    return null
  }
}

/**
 * Safely removes a value from the specified storage
 * @param key - The storage key to remove
 * @param storageType - The type of storage to use
 */
export const removeFromStorage = (key: string, storageType: StorageType = StorageType.SESSION): void => {
  if (storageType === StorageType.LOCAL) {
    removeFromLocalStorage(key)
  } else {
    removeFromSessionStorage(key)
  }
}
